export type CanonicalTreeHistoricalAuthorityConsolidationStatus =
  | 'HISTORICAL_MILESTONE_TRUTH_PRESERVED'
  | 'CURRENT_PHYSICAL_DELETION_AUTHORITY';

export interface CanonicalTreeHistoricalAuthorityConsolidationDecision {
  readonly milestone:
    | 'CC-TREE-ARCH-054'
    | 'CC-TREE-ARCH-055'
    | 'CC-TREE-ARCH-056'
    | 'CC-TREE-ARCH-057'
    | 'CC-TREE-ARCH-058'
    | 'CC-TREE-ARCH-059'
    | 'CC-TREE-ARCH-060'
    | 'CC-TREE-ARCH-061'
    | 'CC-TREE-ARCH-062'
    | 'CC-TREE-ARCH-063'
    | 'CC-TREE-ARCH-064'
    | 'CC-TREE-ARCH-065'
    | 'CC-TREE-ARCH-066';

  readonly consolidationStatus:
    CanonicalTreeHistoricalAuthorityConsolidationStatus;

  readonly historicalAuthoritySourcePreserved:
    true;

  readonly historicalDocumentationPreserved:
    true;

  readonly historicalVerifierPreserved:
    true;

  readonly originalDecisionMayBeRewritten:
    false;

  readonly representsCurrentPhysicalSourceState:
    boolean;

  readonly supersededByMilestone:
    'CC-TREE-ARCH-066' | null;
}

export interface CanonicalTreePostDeletionHistoricalAuthorityConsolidation {
  readonly milestone:
    'CC-TREE-ARCH-067';

  readonly authority:
    'POST_DELETION_HISTORICAL_AUTHORITY_CONSOLIDATION';

  readonly decision:
    'HISTORICAL_MILESTONE_TRUTH_PRESERVED_WHILE_ARCH_066_REMAINS_CURRENT_PHYSICAL_DELETION_AUTHORITY';

  readonly historicalMilestoneCount:
    12;

  readonly currentPhysicalDeletionAuthorityCount:
    1;

  readonly consolidatedAuthorityCount:
    13;

  readonly currentPhysicalDeletionMilestone:
    'CC-TREE-ARCH-066';

  readonly historicalAuthoritiesRewritten:
    false;

  readonly historicalDocumentsRewritten:
    false;

  readonly historicalVerifiersDeleted:
    false;

  readonly arch056ArchiveChanged:
    false;

  readonly arch062ArchiveChanged:
    false;

  readonly physicalRepositorySourceCount:
    0;

  readonly immutableHistoricalTruthPreserved:
    true;

  readonly registryOnlyProductionAuthorityPreserved:
    true;

  readonly staleHistoricalLanguageMayBeInterpretedAsCurrentState:
    false;

  readonly certifiesTreeHealth:
    false;

  readonly certifiesReleaseReadiness:
    false;

  readonly decisions:
    readonly CanonicalTreeHistoricalAuthorityConsolidationDecision[];

  readonly nextGovernedDependency:
    'POST_DELETION_HISTORICAL_AUTHORITY_READ_MODEL';
}

const historicalMilestones =
  Object.freeze([
    'CC-TREE-ARCH-054',
    'CC-TREE-ARCH-055',
    'CC-TREE-ARCH-056',
    'CC-TREE-ARCH-057',
    'CC-TREE-ARCH-058',
    'CC-TREE-ARCH-059',
    'CC-TREE-ARCH-060',
    'CC-TREE-ARCH-061',
    'CC-TREE-ARCH-062',
    'CC-TREE-ARCH-063',
    'CC-TREE-ARCH-064',
    'CC-TREE-ARCH-065',
  ] as const);

export const resolveCanonicalTreePostDeletionHistoricalAuthorityConsolidation =
  (): CanonicalTreePostDeletionHistoricalAuthorityConsolidation =>
    Object.freeze({
      milestone:
        'CC-TREE-ARCH-067',

      authority:
        'POST_DELETION_HISTORICAL_AUTHORITY_CONSOLIDATION',

      decision:
        'HISTORICAL_MILESTONE_TRUTH_PRESERVED_WHILE_ARCH_066_REMAINS_CURRENT_PHYSICAL_DELETION_AUTHORITY',

      historicalMilestoneCount:
        12,

      currentPhysicalDeletionAuthorityCount:
        1,

      consolidatedAuthorityCount:
        13,

      currentPhysicalDeletionMilestone:
        'CC-TREE-ARCH-066',

      historicalAuthoritiesRewritten:
        false,

      historicalDocumentsRewritten:
        false,

      historicalVerifiersDeleted:
        false,

      arch056ArchiveChanged:
        false,

      arch062ArchiveChanged:
        false,

      physicalRepositorySourceCount:
        0,

      immutableHistoricalTruthPreserved:
        true,

      registryOnlyProductionAuthorityPreserved:
        true,

      staleHistoricalLanguageMayBeInterpretedAsCurrentState:
        false,

      certifiesTreeHealth:
        false,

      certifiesReleaseReadiness:
        false,

      decisions:
        Object.freeze([
          ...historicalMilestones.map(
            milestone =>
              Object.freeze({
                milestone,

                consolidationStatus:
                  'HISTORICAL_MILESTONE_TRUTH_PRESERVED' as const,

                historicalAuthoritySourcePreserved:
                  true as const,

                historicalDocumentationPreserved:
                  true as const,

                historicalVerifierPreserved:
                  true as const,

                originalDecisionMayBeRewritten:
                  false as const,

                representsCurrentPhysicalSourceState:
                  false,

                supersededByMilestone:
                  'CC-TREE-ARCH-066' as const,
              }),
          ),

          Object.freeze({
            milestone:
              'CC-TREE-ARCH-066' as const,

            consolidationStatus:
              'CURRENT_PHYSICAL_DELETION_AUTHORITY' as const,

            historicalAuthoritySourcePreserved:
              true as const,

            historicalDocumentationPreserved:
              true as const,

            historicalVerifierPreserved:
              true as const,

            originalDecisionMayBeRewritten:
              false as const,

            representsCurrentPhysicalSourceState:
              true,

            supersededByMilestone:
              null,
          }),
        ]),

      nextGovernedDependency:
        'POST_DELETION_HISTORICAL_AUTHORITY_READ_MODEL',
    });
